import {
  Outlet,
  Link,
  createRootRoute,
  HeadContent,
  Scripts,
  useLocation,
} from "@tanstack/react-router";
import { LazyMotion, MotionConfig } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Toaster } from "@/components/ui/sonner";
import { ConsentBanner } from "@/components/site/ConsentBanner";

import { jsonLd, siteGraph, ORG_DESCRIPTION } from "@/lib/seo";
import { initAnalytics, trackPageView } from "@/lib/analytics";

import appCss from "../styles.css?url";
import dmSerifRegular from "@/fonts/dm-serif-display-400.woff2?url";
import dmSerifItalic from "@/fonts/dm-serif-display-400-italic.woff2?url";
import workSansVar from "@/fonts/work-sans-var.woff2?url";

const loadMotionFeatures = () => import("framer-motion").then((module) => module.domAnimation);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 pt-32">
      <span className="eyebrow">404. Page not found</span>
      <h1 className="mt-4 font-serif text-6xl text-foreground">Off the org chart.</h1>
      <p className="mt-3 max-w-md text-center text-muted-foreground">
        The page you're looking for doesn't exist or has been moved.
      </p>
      <Link
        to="/"
        className="mt-8 inline-flex items-center justify-center rounded-full bg-midnight px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-midnight-deep"
      >
        Return home
      </Link>
    </div>
  );
}

export const Route = createRootRoute({
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Ajility: Fractional COO & Operations Partner for Founders" },
      {
        name: "description",
        content: ORG_DESCRIPTION,
      },
      { name: "author", content: "Ajility" },
      { name: "google-site-verification", content: "t3bcmzhExeyQ0UH6lYz2xYtFx37HxTLyeGbNq61igsg" },
      { name: "msvalidate.01", content: "32A743EB305FE53C17D72DC1A83DB8AA" },
      { property: "og:site_name", content: "Ajility" },
      { property: "og:locale", content: "en_AU" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#1F2A44" },
      { name: "apple-mobile-web-app-title", content: "Ajility" },
      { name: "application-name", content: "Ajility" },
      { name: "msapplication-TileColor", content: "#1F2A44" },
      { name: "msapplication-TileImage", content: "/mstile-150x150.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
      { rel: "icon", type: "image/png", href: "/favicon-16.png", sizes: "16x16" },
      { rel: "icon", type: "image/png", href: "/favicon-32.png", sizes: "32x32" },
      { rel: "icon", type: "image/png", href: "/favicon-48.png", sizes: "48x48" },
      { rel: "icon", type: "image/png", href: "/favicon-96.png", sizes: "96x96" },
      { rel: "icon", type: "image/png", href: "/favicon-192.png", sizes: "192x192" },
      { rel: "icon", type: "image/png", href: "/icon-512.png", sizes: "512x512" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: dmSerifRegular,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: dmSerifItalic,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: workSansVar,
        crossOrigin: "anonymous",
      },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      {
        rel: "alternate",
        type: "text/markdown",
        href: "/llms.txt",
        title: "Plain-text summary of Ajility for AI assistants",
      },
    ],
    scripts: [jsonLd(siteGraph(matches[matches.length - 1]?.pathname))],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-AU">
      <head>
        <HeadContent />
        <style
          dangerouslySetInnerHTML={{
            __html: `@font-face{font-family:"DM Serif Display";font-style:normal;font-weight:400;font-display:swap;src:url("${dmSerifRegular}") format("woff2")}@font-face{font-family:"DM Serif Display";font-style:italic;font-weight:400;font-display:swap;src:url("${dmSerifItalic}") format("woff2")}@font-face{font-family:"Work Sans";font-style:normal;font-weight:300 700;font-display:swap;src:url("${workSansVar}") format("woff2")}`,
          }}
        />
        <noscript>
          <style>{`[style*="opacity:0"]{opacity:1!important;transform:none!important}`}</style>
        </noscript>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const [routeAnnouncement, setRouteAnnouncement] = useState("");

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    // Let the head update before reading the title for the page view.
    const timer = window.setTimeout(
      () => trackPageView(location.pathname, document.title),
      0,
    );
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const isFirstRoute = useRef(true);
  useEffect(() => {
    if (isFirstRoute.current) {
      isFirstRoute.current = false;
      return;
    }
    mainRef.current?.focus();
    setRouteAnnouncement(document.title);
  }, [location.pathname]);

  return (
    <MotionConfig reducedMotion="user">
      <LazyMotion features={loadMotionFeatures} strict>
        <a
          href="#main-content"
          className="fixed left-4 top-4 z-[100] -translate-y-[200%] rounded-full bg-midnight px-4 py-2 text-sm font-medium text-primary-foreground shadow-elevated transition focus:translate-y-0"
        >
          Skip to content
        </a>
        <div className="flex min-h-screen flex-col bg-background">
          <SiteNav />
          <main id="main-content" ref={mainRef} tabIndex={-1} className="flex-1 focus:outline-none">
            <Outlet />
          </main>
          <SiteFooter />
          <div aria-live="polite" className="sr-only">
            {routeAnnouncement}
          </div>
          <Toaster position="top-right" richColors />
          <ConsentBanner />
        </div>
      </LazyMotion>
    </MotionConfig>
  );
}
